package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.documents.PolicyAcknowledgementDto
import com.evoapps.evoshift.data.dto.documents.StaffDocumentDto
import com.evoapps.evoshift.data.dto.documents.WorkplacePolicyDto
import com.evoapps.evoshift.data.mapper.DocumentMapper
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.FirebaseStorageClient
import com.evoapps.evoshift.domain.model.DocumentFiles
import com.evoapps.evoshift.domain.model.PolicyAcknowledgement
import com.evoapps.evoshift.domain.model.StaffDocument
import com.evoapps.evoshift.domain.model.WorkplacePolicy
import com.evoapps.evoshift.domain.repository.DocumentRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlin.time.Instant

/**
 * SH-122: files in Storage through [FirebaseStorageClient] (storage.rules), descriptions in
 * Firestore (firestore.rules), confirmations through the acknowledgePolicy callable (the server's
 * time; clients cannot write them).
 */
class DocumentRepositoryImpl(
    private val firestore: AppFirestore,
    private val storage: FirebaseStorageClient,
    private val functions: CloudFunctionsClient
) : DocumentRepository {

    private fun staffDocuments(userId: String) = firestore.collection("users").document(userId).collection("staff_documents")
    private fun policies(workplaceId: String) = firestore.collection("workplaces").document(workplaceId).collection("policies")
    private fun acknowledgements(workplaceId: String, policyId: String) = policies(workplaceId).document(policyId).collection("acknowledgements")

    override suspend fun getStaffDocuments(userId: String, workplaceId: String): Result<List<StaffDocument>> = runCatching {
        // The query names the workplace, so the HR readers' list passes the rules (resource.data.workplaceId).
        staffDocuments(userId).where { "workplaceId" equalTo workplaceId }.get().documents
            .map { DocumentMapper.toDomain(it.data<StaffDocumentDto>(), it.id, userId) }
    }

    override suspend fun uploadStaffDocument(document: StaffDocument, bytes: ByteArray, onProgress: (Float) -> Unit): Result<StaffDocument> = runCatching {
        val stored = document.copy(id = document.id.ifBlank { staffDocuments(document.userId).document.id })
        storage.upload(stored.storagePath, bytes, stored.contentType, onProgress)
        try {
            staffDocuments(stored.userId).document(stored.id).set(DocumentMapper.toDto(stored))
        } catch (e: Throwable) {
            runCatching { storage.delete(stored.storagePath) }
            throw e
        }
        stored
    }

    override suspend fun deleteStaffDocument(document: StaffDocument): Result<Unit> = runCatching {
        // The file first: storage.rules read the description to let a person remove their own upload.
        storage.delete(document.storagePath)
        staffDocuments(document.userId).document(document.id).delete()
    }

    override suspend fun downloadStaffDocument(document: StaffDocument): Result<ByteArray> = runCatching {
        storage.download(storage.downloadUrl(document.storagePath, null))
    }

    override suspend fun getPolicies(workplaceId: String): Result<List<WorkplacePolicy>> = runCatching {
        policies(workplaceId).get().documents.map { DocumentMapper.toDomain(it.data<WorkplacePolicyDto>(), it.id, workplaceId) }
    }

    override suspend fun publishPolicy(policy: WorkplacePolicy, pdf: ByteArray, onProgress: (Float) -> Unit): Result<WorkplacePolicy> = runCatching {
        val stored = policy.copy(id = policy.id.ifBlank { policies(policy.workplaceId).document.id })
        storage.upload(stored.storagePath, pdf, DocumentFiles.PDF, onProgress)
        try {
            policies(stored.workplaceId).document(stored.id).set(DocumentMapper.toDto(stored))
        } catch (e: Throwable) {
            runCatching { storage.delete(stored.storagePath) }
            throw e
        }
        stored
    }

    override suspend fun deletePolicy(policy: WorkplacePolicy): Result<Unit> = runCatching {
        val acks = acknowledgements(policy.workplaceId, policy.id).get().documents
        if (acks.isNotEmpty()) {
            acks.chunked(400).forEach { chunk ->
                val batch = firestore.batch()
                chunk.forEach { batch.delete(it.reference) }
                batch.commit()
            }
        }
        policies(policy.workplaceId).document(policy.id).delete()
        storage.delete(policy.storagePath)
    }

    override suspend fun downloadPolicy(policy: WorkplacePolicy): Result<ByteArray> = runCatching {
        storage.download(storage.downloadUrl(policy.storagePath, null))
    }

    override suspend fun getAcknowledgements(workplaceId: String, policyId: String): Result<List<PolicyAcknowledgement>> = runCatching {
        acknowledgements(workplaceId, policyId).get().documents
            .mapNotNull { DocumentMapper.toDomain(it.data<PolicyAcknowledgementDto>(), it.id, workplaceId, policyId) }
    }

    override suspend fun getAcknowledgement(workplaceId: String, policyId: String, userId: String): Result<PolicyAcknowledgement?> = runCatching {
        acknowledgements(workplaceId, policyId).document(userId).get().takeIf { it.exists }
            ?.let { DocumentMapper.toDomain(it.data<PolicyAcknowledgementDto>(), userId, workplaceId, policyId) }
    }

    override suspend fun acknowledge(workplaceId: String, policyId: String): Result<PolicyAcknowledgement> = runCatching {
        val result = functions.call("acknowledgePolicy", buildJsonObject {
            put("workplaceId", workplaceId)
            put("policyId", policyId)
        }) as? JsonObject ?: error("acknowledgePolicy answered nothing")
        fun text(key: String) = result[key]?.jsonPrimitive?.contentOrNull
        PolicyAcknowledgement(
            policyId = policyId,
            workplaceId = workplaceId,
            userId = text("userId") ?: error("acknowledgePolicy answered no userId"),
            acknowledgedAt = Instant.parse(text("acknowledgedAt") ?: error("acknowledgePolicy answered no time")),
            displayName = text("displayName")
        )
    }
}
